import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Magnetic } from "./MagneticButton";
import { LivePulseBadge } from "./LivePulseBadge";
import { GrainOverlay } from "./GrainOverlay";
import { HeroProcessRail } from "./HeroProcessRail";
import { PulseExplainerDialog } from "./PulseExplainerDialog";
import { track } from "@/lib/analytics";
import showreelWebm from "../../../../public/video/fenny-showreel.webm.asset.json";
import showreelMp4 from "../../../../public/video/fenny-showreel.mp4.asset.json";

const POSTER = "/video/fenny-showreel-poster.jpg";
// AVIF-postern är 12 kB mot jpg:ens 34 kB. Videon visas bara på desktop där
// AVIF stöds brett, så vi slipper ladda jpg-varianten i onödan.
const POSTER_AVIF = "/video/fenny-showreel-poster.avif";

// Headline split into three lines for typographic dramaturgy.
// Each entry: { text, words: [{ w, kind? }] }
const headlineLines: { words: { w: string; kind?: "italic" | "strike" }[] }[] = [
  { words: [{ w: "Film" }, { w: "som" }, { w: "bygger" }, { w: "varumärke,", kind: "italic" }] },
  { words: [{ w: "driver" }, { w: "leads", kind: "italic" }] },
  { words: [{ w: "och" }, { w: "fortsätter" }, { w: "prestera.", kind: "italic" }] },
];

const ease = [0.16, 1, 0.3, 1] as const;

export function HeroSignature() {
  const ref = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const reduce = useReducedMotion();

  // Lazy video activation — only after IntersectionObserver + idle.
  // The <video> element only exists on md+ viewports (see markup below),
  // so we don't need a JS mobile check that would cause hydration mismatch.
  useEffect(() => {
    if (reduce) return;
    const el = videoRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          const start = () => {
            if (!el.src) {
              // Formatförhandling: hämta bara ett format, webm när det stöds.
              const webmOk = el.canPlayType('video/webm; codecs="vp9,opus"') === "probably"
                || el.canPlayType("video/webm") === "probably";
              el.src = webmOk ? showreelWebm.url : showreelMp4.url;
              el.load();
            }
            el.play().catch(() => {});
          };
          if ("requestIdleCallback" in window) {
            (window as Window & {
              requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => number;
            }).requestIdleCallback(start, { timeout: 1500 });
          } else {
            setTimeout(start, 300);
          }
          io.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce]);

  // Scroll-driven cinematic transforms — gentle parallax + letterbox-out + blur.
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "20%"]);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1.02, reduce ? 1.02 : 1.08]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-12%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.65], [1, 0]);
  // Letterbox: bars start at 0vh and grow to 22vh as you scroll out.
  const barH = useTransform(scrollYProgress, [0, 1], ["0vh", reduce ? "0vh" : "22vh"]);
  const videoBlur = useTransform(scrollYProgress, [0, 1], ["0px", reduce ? "0px" : "6px"]);
  const videoOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.45]);

  return (
    <section
      ref={ref}
      className="relative isolate flex min-h-[100svh] items-end overflow-hidden pt-32"
      aria-label="Premiumfilm på abonnemang"
    >
      {/* ───────── Video stage ───────── */}
      <motion.div
        style={{ y: videoY, scale: videoScale, opacity: videoOpacity }}
        className="pointer-events-none absolute inset-0 -z-10"
      >
        {/* Mobile / reduced-motion: static poster (Ken Burns).
            Rendered via CSS to keep SSR and client output identical. */}
        <picture className={`block h-full w-full ${reduce ? "" : "md:hidden"}`}>
          <source srcSet="/video/fenny-showreel-poster.avif" type="image/avif" />
          <img
            src={POSTER}
            alt=""
            aria-hidden
            fetchPriority="high"
            width={1280}
            height={720}
            className={`h-full w-full object-cover ${reduce ? "" : "animate-[kenburns_22s_ease-in-out_infinite_alternate]"}`}
          />
        </picture>

        {/* Desktop: showreel video. Hidden entirely when reduced-motion.
            Källan sätts i JS efter formatförhandling — bara ett format hämtas. */}
        {!reduce && (
          <video
            ref={videoRef}
            muted
            loop
            playsInline
            preload="none"
            poster={POSTER_AVIF}
            className="hidden h-full w-full object-cover md:block"
            aria-hidden
          />
        )}

        {/* Cinematic blur on scroll-out */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ backdropFilter: `blur(${videoBlur.get()}px)` }}
        />

        {/* Color grade — Fenny butter / mint / peach kiss */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 mix-blend-soft-light opacity-60"
          style={{
            background:
              "radial-gradient(ellipse at 18% 22%, color-mix(in oklab, var(--mint) 22%, transparent) 0%, transparent 55%), radial-gradient(ellipse at 82% 78%, color-mix(in oklab, var(--peach) 18%, transparent) 0%, transparent 55%)",
          }}
        />

        {/* Readability gradient — darker at bottom where text sits */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, color-mix(in oklab, var(--background) 35%, transparent) 0%, color-mix(in oklab, var(--background) 25%, transparent) 35%, color-mix(in oklab, var(--background) 70%, transparent) 70%, var(--background) 100%)",
          }}
        />

        {/* Vignette */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 45%, color-mix(in oklab, var(--background) 70%, transparent) 100%)",
          }}
        />

        <GrainOverlay opacity={0.07} />
      </motion.div>


      {/* ───────── Letterbox bars (short intro, no LCP block) ───────── */}
      <motion.div
        aria-hidden
        initial={reduce ? { height: 0 } : { height: "6vh" }}
        animate={{ height: 0 }}
        transition={{ duration: 0.5, ease, delay: 0 }}
        className="pointer-events-none absolute inset-x-0 top-0 z-20 bg-background"
      />
      <motion.div
        aria-hidden
        initial={reduce ? { height: 0 } : { height: "6vh" }}
        animate={{ height: 0 }}
        transition={{ duration: 0.5, ease, delay: 0 }}
        className="pointer-events-none absolute inset-x-0 bottom-0 z-20 bg-background"
      />
      {/* Scroll-driven letterbox return */}
      <motion.div
        aria-hidden
        style={{ height: barH }}
        className="pointer-events-none absolute inset-x-0 top-0 z-[15] bg-background"
      />
      <motion.div
        aria-hidden
        style={{ height: barH }}
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[15] bg-background"
      />


      {/* ───────── Content ───────── */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="container-wide relative z-10 pb-16 md:pb-24"
      >
        {/* Eyebrow */}
        <motion.div
          initial={reduce ? false : { opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease, delay: 0.2 }}
          className="mb-7 inline-flex"
        >
          <LivePulseBadge tone="mint">Premiumfilm på abonnemang</LivePulseBadge>
        </motion.div>

        {/* Headline — line-by-line masked reveal */}
        <h1
          className="max-w-[18ch] text-balance text-[clamp(2rem,6.8vw,4.6rem)] font-bold leading-[1.05] tracking-[-0.03em] text-butter sm:leading-[1.02] sm:tracking-[-0.035em]"
          aria-label="Film som bygger varumärke, driver leads och fortsätter prestera."
        >
          {headlineLines.map((line, li) => (
            <span key={li} className="block pb-[0.12em]">
              <span className="block">
                {line.words.map((word, wi) => (
                  <span
                    key={wi}
                    className={
                      word.kind === "italic"
                        ? "font-serif font-normal italic text-mint"
                        : word.kind === "strike"
                          ? "relative text-foreground/55"
                          : ""
                    }
                  >
                    {word.kind === "strike" ? (
                      <span className="relative">
                        {word.w}
                        <motion.span
                          aria-hidden
                          initial={reduce ? { scaleX: 1 } : { scaleX: 0 }}
                          animate={{ scaleX: 1 }}
                          transition={{ duration: 0.6, ease, delay: 0.35 + li * 0.05 }}
                          style={{ transformOrigin: "left" }}
                          className="absolute left-0 right-0 top-[55%] block h-[0.08em] bg-foreground/60"
                        />
                      </span>
                    ) : (
                      word.w
                    )}
                    {wi < line.words.length - 1 && " "}
                  </span>
                ))}
              </span>
            </span>
          ))}

        </h1>

        {/* Sub */}
        <motion.p
          initial={reduce ? false : { opacity: 0.001, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease, delay: 0.2 }}
          className="lede mt-6 max-w-xl text-pretty text-[15px] leading-[1.5] text-foreground/85 sm:mt-8 sm:text-base md:text-[17px]"
          data-quick-answer
          data-speakable
        >

          Fenny hjälper tillväxtbolag att skapa, paketera och optimera rörligt
          innehåll för webb, sociala medier, sälj och annonsering. Fast månadspris,
          tydlig process och första klippet inom 10–14 dagar.
        </motion.p>

        {/* CTA */}
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease, delay: 1.2 }}
          className="mt-9 flex flex-wrap items-center gap-x-5 gap-y-3"
        >
          <Magnetic strength={0.28}>
            <Link
              to="/kontakt"
              onClick={() => track("hero_primary_cta", { location: "hero", label: "Boka strategimöte" })}
              className="group relative inline-flex h-14 items-center gap-3 rounded-full bg-peach px-8 text-base font-semibold leading-none text-peach-foreground shadow-[0_16px_40px_-12px] shadow-peach/35 ring-1 ring-peach/40 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_24px_60px_-12px] hover:shadow-peach/55"
            >
              Boka strategimöte
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Magnetic>

          <Link
            to="/priser"
            onClick={() => track("hero_secondary_cta", { location: "hero", label: "Se priser" })}
            className="group inline-flex h-14 items-center gap-2 rounded-full border border-white/20 bg-background/20 px-8 text-base font-semibold leading-none text-foreground/90 backdrop-blur transition-colors hover:border-mint/50 hover:bg-mint/10 hover:text-mint"
          >
            Se priser
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>

          <div className="hidden sm:block">
            <PulseExplainerDialog
              trigger={
                <button
                  type="button"
                  onClick={() => track("hero_tertiary_cta", { location: "hero", label: "Så funkar Pulse" })}
                  className="group inline-flex h-14 items-center gap-2 rounded-full px-4 text-sm font-medium leading-none text-foreground/70 transition-colors hover:text-mint"
                >
                  Så funkar Pulse
                  <span className="transition-transform group-hover:translate-y-0.5" aria-hidden>↓</span>
                </button>
              }
            />
          </div>
        </motion.div>

        {/* Process rail — desktop only, too heavy on mobile */}
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 1.5 }}
          className="mt-10 hidden md:mt-14 md:block"
        >
          <HeroProcessRail />
        </motion.div>
      </motion.div>

      {/* Ken Burns keyframes for mobile poster */}
      <style>{`
        @keyframes kenburns {
          0% { transform: scale(1.05) translate3d(0, 0, 0); }
          100% { transform: scale(1.14) translate3d(-1.5%, -1%, 0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-\\[kenburns_22s_ease-in-out_infinite_alternate\\] { animation: none !important; }
        }
      `}</style>
    </section>
  );
}
